/**
 * Cache - Simple in-memory cache implementation
 * Provides methods for storing, retrieving, and managing cached data
 */

export default class Cache {
    /**
     * Constructor for Cache
     * @param {number} capacity - Maximum number of items to store in the cache
     */
    constructor(capacity = 1000) {
        this.capacity = capacity;
        this.cache = {};
        this.keys = [];
        this.timestamps = {};
    }

    /**
     * Get a value from the cache
     * @param {string} key - The key to retrieve
     * @returns {*} - The cached value or undefined if not found
     */
    get(key) {
        if (this.cache[key] !== undefined) {
            // Update timestamp on access to implement LRU behavior
            this.timestamps[key] = Date.now();
            return this.cache[key];
        }
        return undefined;
    }

    /**
     * Set a value in the cache
     * @param {string} key - The key to store
     * @param {*} value - The value to store
     * @param {number} ttl - Time to live in milliseconds (optional)
     */
    set(key, value, ttl = 0) {
        // If the key doesn't exist, add it to the keys array
        if (this.cache[key] === undefined) {
            this.keys.push(key);
            
            // If we're over capacity, remove the least recently used item
            if (this.keys.length > this.capacity) {
                this.evictLRU();
            }
        }
        
        // Store the value and update the timestamp
        this.cache[key] = value;
        this.timestamps[key] = Date.now();
        
        // If TTL is specified, set up expiration
        if (ttl > 0) {
            setTimeout(() => {
                this.remove(key);
            }, ttl);
        }
    }

    /**
     * Remove a value from the cache
     * @param {string} key - The key to remove
     * @returns {boolean} - True if the key was removed, false otherwise
     */
    remove(key) {
        if (this.cache[key] !== undefined) {
            delete this.cache[key];
            delete this.timestamps[key];
            
            const index = this.keys.indexOf(key);
            if (index !== -1) {
                this.keys.splice(index, 1);
            }
            
            return true;
        }
        return false;
    }

    /**
     * Clear all items from the cache
     */
    clear() {
        this.cache = {};
        this.keys = [];
        this.timestamps = {};
    }

    /**
     * Get the number of items in the cache
     * @returns {number} - The number of items in the cache
     */
    size() {
        return this.keys.length;
    }

    /**
     * Check if a key exists in the cache
     * @param {string} key - The key to check
     * @returns {boolean} - True if the key exists, false otherwise
     */
    has(key) {
        return this.cache[key] !== undefined;
    }

    /**
     * Get all keys in the cache
     * @returns {Array} - Array of keys
     */
    getAllKeys() {
        return [...this.keys];
    }

    /**
     * Evict the least recently used item from the cache
     * @private
     */
    evictLRU() {
        if (this.keys.length === 0) {
            return;
        }
        
        // Find the key with the oldest timestamp
        let oldestKey = this.keys[0];
        let oldestTime = this.timestamps[oldestKey];
        
        for (let i = 1; i < this.keys.length; i++) {
            const key = this.keys[i];
            const time = this.timestamps[key];
            
            if (time < oldestTime) {
                oldestKey = key;
                oldestTime = time;
            }
        }
        
        // Remove the oldest item
        this.remove(oldestKey);
    }
}
